import { Box, Flex, Toast } from '@modusign/design-system';
import { useCreateTemplate, useUpdateTemplate } from '@modusign/openapi-client-template';
import {
  CreateTemplateRequestDto,
  UpdateTemplateRequestDto,
} from '@modusign/openapi-client-template/model';
import { useDocument } from '@modusign/react-signing';
import { useSigningState } from '@modusign/react-signing/setup';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router';

import { TextFieldFontLoader } from '@app/components/TextField';
import AttachmentExceededAlertModal from '@app/containers/Modal/AttachmentExceededAlertModal';
import RequesterAttachmentsCard from '@app/features/apply-react-signing/components/RequesterAttachmentCard';
import { toDocumentModelForSetup } from '@app/features/apply-react-signing/libs/signing-mapper';
import { templateMutationErrorHandler } from '@app/features/apply-react-signing/libs/template/errorHandler';
import TemplateCopySaveModal from '@app/features/template/components/TemplateCopySaveModal';
import ViewModeInlineAlert from '@app/features/template/components/TemplateEditPages/ViewModeInlineAlert';
import TemplateEditSaveFooter from '@app/features/template/components/TemplateEditSaveFooter';
import TemplateHeader from '@app/features/template/components/TemplateHeader';
import TemplateLabelInputCard from '@app/features/template/components/TemplateLabelInputCard';
import TemplateLoadingSpinner from '@app/features/template/components/TemplateLoadingSpinner';
import TemplateSignerDetailSetUpCard from '@app/features/template/components/TemplateSignerDetailSetUpCard';
import TemplateTitleInputCard from '@app/features/template/components/TemplateTitleInputCard';
import useTemplateOrdering from '@app/features/template/hooks/useTemplateOrdering';
import useTemplatePermission from '@app/features/template/hooks/useTemplatePermission';
import validateTemplateTitle from '@app/features/template/libs/validateTemplateTitle';
import { usePDF } from '@app/hooks/usePDF';
import useUsageCount from '@app/hooks/useUsageCount';
import { useWorkspaceId } from '@app/hooks/useWorkspaceId';
import pushDataLayer from '@app/libs/analytics/googleTagManager';
import embeddedService from '@app/libs/embedded/embeddedService';
import redirect from '@app/libs/embedded/redirect';
import { pdfToBase64 } from '@app/libs/pdf/pdfToBase64';
import PDFDocumentPageLayout from '@app/libs/reactPdfJs/components/PDFDocumentPageLayout';
import { zoomIn, zoomOut } from '@app/libs/reactPdfJs/store/actions';
import { getPdfMaxPage } from '@app/libs/reactPdfJs/store/selectors';
import workspaceService from '@app/libs/workspaceService';
import { deletePageOverRequesterFields } from '@app/store/documentSetup/actions';
import { getRequesterFieldMaxPage } from '@app/store/documentSetup/selectors';
import getTemplateAttachmentLimitExceeded from '@app/store/selectors/getTemplateAttachmentLimitExceeded';
import getTemplateAttachmentLimitReached from '@app/store/selectors/getTemplateAttachmentLimitReached';
import {
  deletePageOverParticipantFields,
  templateLoading,
  templateLoadingFinish,
} from '@app/store/template/actions';
import {
  fileIdSelector,
  idSelector,
  idsInPagesSelector,
  labelIdsSelector,
  originTitleSelector,
} from '@app/store/template/selectors';
import { getTemplateById } from '@app/store/transport-layer/template';
import {
  documentUpload,
  postDocumentUpload,
} from '@app/store/transport-layer/upload/document/upload';

interface TemplateGeneralEditPageProps {
  onPrevRouteClick: () => void;
}

function TemplateGeneralEditPage({ onPrevRouteClick }: TemplateGeneralEditPageProps) {
  const dispatch = useDispatch();
  const { document, setTitle } = useDocument();
  const { initOrdering } = useTemplateOrdering();

  const workspaceId = useWorkspaceId();
  const { id } = useParams<{ id: string }>();

  const { editTemplatePermission, editSharedTemplatePermission } = useTemplatePermission({
    resourceRoleQueryOption: {
      templateId: id,
      options: {
        enabled: !!workspaceId,
      },
    },
  });
  const { templateCount } = useUsageCount();

  const fileId = useSelector(fileIdSelector);
  const fieldIdsInPages = useSelector(idsInPagesSelector);
  const hasAttachmentLimitExceeded = useSelector(getTemplateAttachmentLimitExceeded);
  const hasAttachmentLimitReached = useSelector(getTemplateAttachmentLimitReached);
  const pdfMaxPage = useSelector(getPdfMaxPage);
  const requesterFieldsMaxPage = useSelector(getRequesterFieldMaxPage);
  const originTitle = useSelector(originTitleSelector);
  const labelIds = useSelector(labelIdsSelector);
  const history = useHistory();
  const templateId = useSelector(idSelector);
  const { signingState } = useSigningState();

  const [isAttachmentExceededAlertModalOpen, setAttachmentExceededAlertModalOpen] = useState(false);
  const [error, setError] = useState('');
  const [isOpenTemplateCopySaveModal, setIsOpenTemplateCopySaveModal] = useState(false);

  const { pdf } = usePDF();

  const handleTitleValidationError = (title: string) => {
    const { message } = validateTemplateTitle(title);
    setError(message);
  };

  useEffect(() => {
    handleTitleValidationError(document.title);
  }, [document.title]);

  // TODO(@DevSDK): 아래 라인 불필요
  useEffect(() => {
    const participantFieldPages = Object.keys(fieldIdsInPages).map(
      (page) => parseInt(page, 10) ?? 0
    );
    const participantFieldMaxPage = Math.max(...participantFieldPages);
    if (participantFieldMaxPage > pdfMaxPage && pdfMaxPage > 0) {
      dispatch(deletePageOverParticipantFields({ maxPage: pdfMaxPage }));
    }
  }, [dispatch, fieldIdsInPages, pdfMaxPage]);

  useEffect(() => {
    if (requesterFieldsMaxPage > pdfMaxPage && pdfMaxPage > 0) {
      dispatch(deletePageOverRequesterFields({ maxPage: pdfMaxPage }));
    }
  }, [dispatch, pdfMaxPage, requesterFieldsMaxPage]);

  const { mutate: updateTemplate } = useUpdateTemplate({
    mutation: {
      onSuccess: () => {
        initOrdering();
        dispatch(templateLoadingFinish());
        Toast.success({ description: '템플릿을 수정했습니다.' });

        if (embeddedService.isEmbedded) {
          redirect(embeddedService.redirectUrl, {
            query: {
              templateId,
              eventType: 'template_edited',
            },
          });
        } else {
          history.push('/templates');
        }
      },
      onError: (error, { data }) => {
        dispatch(templateLoadingFinish());
        templateMutationErrorHandler(dispatch, error, data, templateCount.limit);
      },
    },
  });

  const { mutate: createTemplate } = useCreateTemplate({
    mutation: {
      onSuccess: () => {
        pushDataLayer({ event: 'createTemplate' });
        initOrdering();
        dispatch(templateLoadingFinish());
        Toast.success({ description: '템플릿의 사본을 만들었습니다.' });
        if (embeddedService.isEmbedded) {
          redirect(embeddedService.redirectUrl, {
            query: {
              templateId,
              eventType: 'template_created',
            },
          });
        } else {
          history.push('/templates');
        }
      },
      onError: (error, { data }) => {
        dispatch(templateLoadingFinish());
        templateMutationErrorHandler(dispatch, error, data, templateCount.limit);
      },
    },
  });

  async function uploadPdf(pdf: ArrayBuffer) {
    const pdfAsBase64 = await pdfToBase64(pdf);
    const { fileId: id } = await postDocumentUpload({ base64: pdfAsBase64 });
    return id;
  }

  const handleSaveClick = async () => {
    if (hasAttachmentLimitExceeded) {
      setAttachmentExceededAlertModalOpen(true);
      return;
    }
    dispatch(templateLoading());

    let newFileId = fileId;
    const fileChanged = fileId && pdf;
    if (fileChanged) {
      newFileId = await uploadPdf(pdf);
    }

    const data = toDocumentModelForSetup(signingState);
    updateTemplate({
      id: templateId,
      workspaceId: workspaceService.getWorkspaceId(),
      data: {
        attachments: data.attachments,
        participants: data.participants,
        fieldGroups: data.fieldGroups,
        fields: data.fields,
        verifications: data.verifications,
        title: document.title,
        file: {
          id: newFileId,
        },
        labelIds,
      } as UpdateTemplateRequestDto,
    });
  };

  const saveTemplateAsCopy = async (title: string) => {
    dispatch(templateLoading());
    let saveFileId = fileId;
    if (!saveFileId) {
      const originTemplateData = await getTemplateById(
        workspaceService.getWorkspaceId(),
        templateId
      );
      const { fileId: copiedFileId } = await documentUpload({
        name: `${title}.pdf`,
        url: originTemplateData.file.url,
      });
      saveFileId = copiedFileId;
    }

    const data = toDocumentModelForSetup(signingState);

    let newFileId = fileId;
    if (pdf) {
      newFileId = await uploadPdf(pdf);
    }

    createTemplate({
      workspaceId: workspaceService.getWorkspaceId(),
      data: {
        attachments: data.attachments,
        participants: data.participants,
        fieldGroups: data.fieldGroups,
        fields: data.fields,
        verifications: data.verifications,
        title,
        file: { id: newFileId },
        labelIds,
      } as CreateTemplateRequestDto,
    });
  };

  const handleSaveAsCopyClick = () => {
    if (hasAttachmentLimitExceeded) {
      setAttachmentExceededAlertModalOpen(true);
      return;
    }
    if (originTitle === document.title) {
      setIsOpenTemplateCopySaveModal(true);
    } else {
      saveTemplateAsCopy(document.title);
    }
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
  };

  const handleZoomIn = () => {
    dispatch(zoomIn());
  };

  const handleZoomOut = () => {
    dispatch(zoomOut());
  };
  return (
    <PDFDocumentPageLayout>
      <TextFieldFontLoader />
      <TemplateLoadingSpinner />
      <TemplateHeader
        currentStepIndex={3}
        isExchangeDocumentVisible={editTemplatePermission.isPermitted}
        progressStep="templateGeneralSetup"
        onPDFZoomIn={handleZoomIn}
        onPDFZoomOut={handleZoomOut}
      />
      <Box flex="1" minHeight="0" overflow="auto">
        <Flex flexDirection="column" gap={4} maxWidth="xs" mx="auto" pb="86px" pt={4} px={4}>
          <TemplateTitleInputCard
            error={error}
            handleTitleChange={handleTitleChange}
            title={document.title || ''}
          />
          <RequesterAttachmentsCard
            displayDataLabel
            hasAttachmentLimitReached={hasAttachmentLimitReached}
          />
          <TemplateLabelInputCard labelIds={labelIds} />
          <TemplateSignerDetailSetUpCard />
        </Flex>
      </Box>
      <TemplateEditSaveFooter
        backButtonText="이전 단계로"
        editTemplatePermission={editTemplatePermission.isPermitted}
        error={!!error}
        onBackStepClick={onPrevRouteClick}
        onSaveAsCopyClick={handleSaveAsCopyClick}
        onSaveClick={handleSaveClick}
      />
      {isAttachmentExceededAlertModalOpen && (
        <AttachmentExceededAlertModal
          onConfirm={() => {
            setAttachmentExceededAlertModalOpen(false);
          }}
        />
      )}
      {isOpenTemplateCopySaveModal && (
        <TemplateCopySaveModal
          onClickCancel={() => {
            setIsOpenTemplateCopySaveModal(false);
          }}
          onClickSave={(title: string) => {
            saveTemplateAsCopy(title);
            setIsOpenTemplateCopySaveModal(false);
          }}
        />
      )}
      {!editTemplatePermission.isPermitted && !editSharedTemplatePermission && (
        <ViewModeInlineAlert />
      )}
    </PDFDocumentPageLayout>
  );
}

export default TemplateGeneralEditPage;
